import {FC, useCallback, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {IconButton, Stack} from '@mui/material';
import {Delete, HttpsOutlined} from '@mui/icons-material';
import {EditIcon} from '../../../icons/EditIcon';
import {useAppDispatch} from '../../../../app/hooks';
import {tagsApi} from '../../../../api';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {RfidActionsForDedicatedPageProps} from './RfidActionsForDedicatedPage.types';
import {rfidActionsSx} from './RfidActionsForDedicatedPage.sx';
import AMPModal from '../../AMPModal';
import {useIntl} from 'react-intl';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {CreateRfidFormFromDedicatedPage} from 'src/components/forms/CreateRfidFormFromDedicatedPage';

export const RfidActionsForDedicatedPage: FC<RfidActionsForDedicatedPageProps> = ({
                                                                                      businessId,
                                                                                      ocppTagId,
                                                                                      blocked,
                                                                                      idTabObject,
                                                                                  }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();


    const [triggerDeleteTag] = tagsApi.useDeleteTagMutation();
    const [triggerToggleTag] = tagsApi.useToggleTagStatusMutation();

    const [showUpdateModal, setShowUpdateModal] = useState(false);

    const handleShowUpdateModal = (status: boolean) => {
        return () => {
            setShowUpdateModal(status);
        };
    };

    const handleRfidDelete = async () => {
        try {
            dispatch(preloaderChangeStatus(true));
            if (!ocppTagId || !businessId) {
                return;
            }

            await triggerDeleteTag({
                params: {
                    businessId,
                },
                tagId: ocppTagId,
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleToggleClick = async () => {
        try {
            dispatch(preloaderChangeStatus(true));
            if (!ocppTagId || !businessId) {
                return;
            }

            await triggerToggleTag({
                params: {
                    businessId,
                    blocked: !blocked,
                },
                tagId: ocppTagId,
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleDeleteClick = useCallback(() => {
        // Always use direct tag deletion - unlinking APIs are deprecated
        handleRfidDelete();
    }, [handleRfidDelete]);

    // Was a list of four role names standing in for a permission, which is what it now asks for.
    const canManageRfid = useCan('rfidTags.update');
    const canDeleteRfid = useCan('rfidTags.delete');

    return (
        <>
            <Stack sx={rfidActionsSx.root}>
                {canManageRfid && (
                    <>
                        <IconButton
                            sx={rfidActionsSx.button}
                            onClick={handleToggleClick}
                        >
                            <HttpsOutlined sx={rfidActionsSx.icon}/>
                        </IconButton>

                        <IconButton
                            sx={rfidActionsSx.button}
                            onClick={handleShowUpdateModal(true)}
                        >
                            <EditIcon/>
                        </IconButton>

                        {/* Its own permission: blocking a card and destroying it are different
                            endpoints, and rfidTags.update does not carry the second. */}
                        {canDeleteRfid && (
                            <IconButton
                                sx={rfidActionsSx.button}
                                onClick={handleDeleteClick}
                            >
                                <Delete sx={rfidActionsSx.icon}/>
                            </IconButton>
                        )}
                    </>
                )}
            </Stack>

            {canManageRfid && (
                <AMPModal
                    handleClose={handleShowUpdateModal(false)}
                    open={showUpdateModal}
                    title={intl.formatMessage({id: 'editRfid'})}
                    type="md"
                    fullWidth
                    withoutPaddings
                >
                    <CreateRfidFormFromDedicatedPage
                        initialValues={{
                            tagId: idTabObject?.id ?? 0,
                            idTag: idTabObject?.idTag ?? '',
                            companyId: idTabObject?.company?.id ?? 0,
                            drivers: idTabObject?.users ?? [],
                            employeeId: idTabObject?.employee?.id ?? 0,
                            blocked: idTabObject?.blocked ?? false,
                            businessId: idTabObject?.business?.id ?? 0,
                        }}
                        handleClose={handleShowUpdateModal(false)}
                    />
                </AMPModal>
            )}
        </>
    );
};
